import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Package, PackageCheck, PackageX, Plus, Search, 
  LogOut, ArrowLeftRight, RotateCcw, Laptop, Mouse, Monitor, Edit2, Trash2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import SidebarLayout from '../components/SidebarLayout';
import ChangeHistoryButton from '../components/ChangeHistoryButton';
import { toast } from 'sonner';

import API from '../utils/api';

const CATEGORIES = ["Laptop", "Accessories", "Equipment", "Furniture", "Other"];

export default function AssetPage() {
  const { user, logout } = useAuth();
  const canWrite = user?.role === 'admin' || user?.effective_permissions?.assets === 'edit';
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedEmployeeAssets, setSelectedEmployeeAssets] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [assetsRes, statsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/assets`, { 
          withCredentials: true,
          params: {
            category: categoryFilter && categoryFilter !== 'all' ? categoryFilter : undefined,
            status: statusFilter && statusFilter !== 'all' ? statusFilter : undefined,
            search: search || undefined
          }
        }),
        axios.get(`${API}/assets/stats/summary`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setAssets(assetsRes.data);
      setStats(statsRes.data);
      setEmployees(employeesRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Group current assignments by employee
  const getEmployeeAssetSummary = () => {
    if (!stats?.current_assignments) return [];
    
    const grouped = {};
    stats.current_assignments.forEach(assignment => {
      const empId = assignment.employee_id;
      if (!grouped[empId]) {
        // Find employee department from employees list
        const emp = employees.find(e => e.employee_id === empId);
        grouped[empId] = {
          employee_id: empId,
          employee_name: assignment.employee_name,
          department: emp?.department || '',
          assets: [],
          asset_count: 0
        };
      }
      grouped[empId].assets.push(assignment);
      grouped[empId].asset_count++;
    });
    
    return Object.values(grouped).sort((a, b) => b.asset_count - a.asset_count);
  };

  if (loading && !stats) {
    return (<SidebarLayout title="Assets" subtitle="Asset Tracker"><div className="flex items-center justify-center py-20"><div className="text-white text-lg">Loading...</div></div></SidebarLayout>);
  }

  return (
    <SidebarLayout title="Assets" subtitle="Asset Tracker">
        {/* KPI Cards */}
        <section className="mb-8 animate-fadeIn">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard
              icon={<Package className="text-indigo-400" />}
              label="Total Assets"
              value={stats?.total_assets || 0}
              color="indigo"
              testId="kpi-total-assets"
            />
            <KPICard
              icon={<PackageCheck className="text-emerald-400" />}
              label="Available"
              value={stats?.available_assets || 0}
              color="emerald"
              testId="kpi-available"
            />
            <KPICard
              icon={<PackageX className="text-amber-400" />}
              label="Assigned"
              value={stats?.assigned_assets || 0}
              color="amber"
              testId="kpi-assigned"
            />
          </div>
        </section>

        {/* Search & Filters */}
        <section className="mb-6 glass-card p-4 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
              <Input
                type="text"
                placeholder="Search by name, ID, or brand..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-black/40 border-white/10 text-white placeholder:text-slate-500"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[150px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10">
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-black/40 border-white/10 text-slate-300" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Assigned">Assigned</SelectItem>
                </SelectContent>
              </Select>

              {canWrite && <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-[0_0_15px_rgba(79,70,229,0.3)]"
                data-testid="add-asset-button"
              >
                <Plus size={18} className="mr-2" />
                Add Asset
              </Button>}
            </div>
          </div>
        </section>

        {/* Tabs */}
        <Tabs defaultValue="assets" className="animate-fadeIn" style={{ animationDelay: '0.2s' }}>
          <TabsList className="bg-white/5 border border-white/10 mb-6">
            <TabsTrigger value="assets" className="data-[state=active]:bg-indigo-600" data-testid="tab-assets">
              All Assets
            </TabsTrigger>
            <TabsTrigger value="current" className="data-[state=active]:bg-indigo-600" data-testid="tab-current">
              Current Assignments
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-indigo-600" data-testid="tab-history">
              History
            </TabsTrigger>
          </TabsList>

          {/* All Assets Tab */}
          <TabsContent value="assets">
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto mobile-cards">
                <table className="data-table" data-testid="assets-table">
                  <thead>
                    <tr>
                      <th>Asset ID</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Brand / Model</th>
                      <th>Status</th>
                      <th>Assigned To</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assets.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-slate-500">
                          No assets found
                        </td>
                      </tr>
                    ) : (
                      assets.map((asset) => (
                        <tr key={asset.id} data-testid={`asset-row-${asset.asset_id}`}>
                          <td>
                            <div className="flex items-center gap-1">
                              <span className="font-mono text-cyan-400">{asset.asset_id}</span>
                              {user?.role === 'admin' && <ChangeHistoryButton resource="assets" recordId={asset.asset_id} />}
                            </div>
                          </td>
                          <td>
                            <div className="flex items-center gap-2">
                              {getCategoryIcon(asset.category)}
                              <span className="text-white font-medium">{asset.name}</span>
                            </div>
                          </td>
                          <td className="text-slate-400">{asset.category}</td>
                          <td className="text-slate-400">
                            {asset.brand || '-'} {asset.model ? `/ ${asset.model}` : ''}
                          </td>
                          <td>
                            <span className={`status-badge ${asset.status.toLowerCase()}`}>
                              {asset.status}
                            </span>
                          </td>
                          <td className="text-slate-300">
                            {asset.assigned_to || '-'}
                          </td>
                          <td>
                            {canWrite ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingAsset(asset)}
                                  className="h-8 w-8 p-0 text-slate-400 hover:text-white"
                                  data-testid={`edit-btn-${asset.asset_id}`}
                                >
                                  <Edit2 size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={async () => {
                                    if (!window.confirm(`Delete "${asset.name}"?`)) return;
                                    try {
                                      await axios.delete(`${API}/assets/${asset.id}`, { withCredentials: true });
                                      toast.success('Asset deleted');
                                      fetchData();
                                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
                                  }}
                                  className="h-8 w-8 p-0 text-slate-400 hover:text-rose-400"
                                  data-testid={`delete-btn-${asset.asset_id}`}
                                  disabled={asset.status === 'Assigned'}
                                >
                                  <Trash2 size={14} />
                                </Button>
                                {asset.status === 'Available' ? (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedAsset(asset);
                                      setShowAssignDialog(true);
                                    }}
                                    className="h-8 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20"
                                    data-testid={`assign-btn-${asset.asset_id}`}
                                  >
                                    <ArrowLeftRight size={14} className="mr-1" />
                                    Assign
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    onClick={async () => {
                                      const res = await axios.get(`${API}/asset-assignments`, {
                                        withCredentials: true,
                                        params: { asset_id: asset.asset_id, active_only: true }
                                      });
                                      if (res.data.length > 0) {
                                        setSelectedAssignment(res.data[0]);
                                        setShowReturnDialog(true);
                                      }
                                    }}
                                    className="h-8 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20"
                                    data-testid={`return-btn-${asset.asset_id}`}
                                  >
                                    <RotateCcw size={14} className="mr-1" />
                                    Return
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500 px-2 py-1 rounded bg-slate-500/10 border border-slate-500/20">View Only</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Current Assignments Tab */}
          <TabsContent value="current">
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="current-assignments-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Department</th>
                      <th>No. of Assets</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {getEmployeeAssetSummary().length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-12 text-slate-500">
                          No current assignments
                        </td>
                      </tr>
                    ) : (
                      getEmployeeAssetSummary().map((summary) => (
                        <tr 
                          key={summary.employee_id} 
                          className="cursor-pointer"
                          onClick={() => setSelectedEmployeeAssets(summary)}
                          data-testid={`employee-assets-${summary.employee_id}`}
                        >
                          <td>
                            <div>
                              <div className="text-white font-medium">{summary.employee_name}</div>
                              <div className="text-xs font-mono text-cyan-400">{summary.employee_id}</div>
                            </div>
                          </td>
                          <td className="text-slate-400">{summary.department || '-'}</td>
                          <td>
                            <span className="text-xl font-bold text-white">{summary.asset_count}</span>
                            <span className="text-slate-500 text-sm ml-1">asset{summary.asset_count !== 1 ? 's' : ''}</span>
                          </td>
                          <td>
                            <span className="text-cyan-400 text-sm">View Details →</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="glass-card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="history-table">
                  <thead>
                    <tr>
                      <th>Asset</th>
                      <th>Employee</th>
                      <th>Allocated</th>
                      <th>Returned</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.recent_history?.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center py-12 text-slate-500">
                          No history found
                        </td>
                      </tr>
                    ) : (
                      stats?.recent_history?.map((record) => (
                        <tr key={record.id} data-testid={`history-${record.id}`}>
                          <td>
                            <div>
                              <div className="text-slate-300">{record.asset_name}</div>
                              <div className="text-xs font-mono text-cyan-400">{record.asset_id}</div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div className="text-white font-medium">{record.employee_name}</div>
                              <div className="text-xs font-mono text-slate-500">{record.employee_id}</div>
                            </div>
                          </td>
                          <td className="text-slate-400">{formatDate(record.allocation_date)}</td>
                          <td className="text-slate-400">{formatDate(record.return_date)}</td>
                          <td>
                            {record.return_date ? (
                              <span className="text-xs px-2 py-1 rounded bg-slate-500/10 text-slate-400">
                                Returned
                              </span>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded bg-amber-500/10 text-amber-400">
                                Active
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

      {/* Create Asset Dialog */}
      {showCreateDialog && (
        <CreateAssetDialog
          onClose={() => setShowCreateDialog(false)}
          onCreated={fetchData}
        />
      )}

      {/* Assign Asset Dialog */}
      {showAssignDialog && selectedAsset && (
        <AssignAssetDialog
          asset={selectedAsset}
          employees={employees}
          onClose={() => {
            setShowAssignDialog(false);
            setSelectedAsset(null);
          }}
          onAssigned={fetchData}
        />
      )}

      {/* Return Asset Dialog */}
      {showReturnDialog && selectedAssignment && (
        <ReturnAssetDialog
          assignment={selectedAssignment}
          onClose={() => {
            setShowReturnDialog(false);
            setSelectedAssignment(null);
          }}
          onReturned={fetchData}
        />
      )}

      {/* Edit Asset Dialog */}
      {editingAsset && (
        <EditAssetDialog
          asset={editingAsset}
          onClose={() => setEditingAsset(null)}
          onUpdated={fetchData}
        />
      )}

      {/* Employee Assets Summary Sheet */}
      {selectedEmployeeAssets && (
        <EmployeeAssetsSummary
          summary={selectedEmployeeAssets}
          onClose={() => setSelectedEmployeeAssets(null)}
          onReturn={(assignment) => {
            setSelectedAssignment(assignment);
            setShowReturnDialog(true);
          }}
          formatDate={formatDate}
        />
      )}
    </SidebarLayout>
  );
}

// Helper function for category icons
function getCategoryIcon(category) {
  const icons = {
    'Laptop': <Laptop size={16} className="text-indigo-400" />,
    'Accessories': <Mouse size={16} className="text-cyan-400" />,
    'Equipment': <Monitor size={16} className="text-emerald-400" />,
    'Furniture': <Package size={16} className="text-amber-400" />,
    'Other': <Package size={16} className="text-slate-400" />
  };
  return icons[category] || icons['Other'];
}

// KPI Card Component
function KPICard({ icon, label, value, color, testId }) {
  const bgColors = {
    indigo: 'border-indigo-500/20',
    emerald: 'border-emerald-500/20',
    amber: 'border-amber-500/20'
  };

  return (
    <div className={`kpi-card border-l-4 ${bgColors[color]}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">
          {label}
        </span>
        {icon}
      </div>
      <div className="font-heading text-4xl font-black tracking-tight text-white">
        {value}
      </div>
    </div>
  );
}

// Create Asset Dialog
function CreateAssetDialog({ onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    brand: '',
    model: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/assets`, formData, { withCredentials: true });
      onCreated();
      onClose();
    } catch (err) {
      console.error('Failed to create asset:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-xl">Add New Asset</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Asset Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="MacBook Pro 14"
              className="bg-black/40 border-white/10 text-white"
              data-testid="create-asset-name"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Category *</label>
            <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="create-asset-category">
                <SelectValue placeholder="Select Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10">
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Brand</label>
            <Input
              value={formData.brand}
              onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
              placeholder="Apple"
              className="bg-black/40 border-white/10 text-white"
              data-testid="create-asset-brand"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Model</label>
            <Input
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              placeholder="M3 Pro"
              className="bg-black/40 border-white/10 text-white"
              data-testid="create-asset-model"
            />
          </div>
          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !formData.name || !formData.category} className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? 'Creating...' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Assign Asset Dialog
function AssignAssetDialog({ asset, employees, onClose, onAssigned }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '',
    allocation_date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/asset-assignments`, {
        asset_id: asset.asset_id,
        employee_id: formData.employee_id,
        allocation_date: formData.allocation_date
      }, { withCredentials: true });
      onAssigned();
      onClose();
    } catch (err) {
      console.error('Failed to assign asset:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-xl">Assign Asset</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="glass-card p-3 mb-4">
            <div className="text-white font-medium">{asset.name}</div>
            <div className="text-xs font-mono text-cyan-400">{asset.asset_id}</div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Assign to Employee *</label>
              <Select value={formData.employee_id} onValueChange={(v) => setFormData({ ...formData, employee_id: v })}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="assign-employee-select">
                  <SelectValue placeholder="Select Employee" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[200px]">
                  {employees.filter(e => e.status === 'Active').map(emp => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.name} ({emp.employee_id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Allocation Date *</label>
              <Input
                type="date"
                value={formData.allocation_date}
                onChange={(e) => setFormData({ ...formData, allocation_date: e.target.value })}
                required
                className="bg-black/40 border-white/10 text-white"
                data-testid="assign-date-input"
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !formData.employee_id} className="bg-emerald-600 hover:bg-emerald-700">
                {loading ? 'Assigning...' : 'Assign Asset'}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Return Asset Dialog
function ReturnAssetDialog({ assignment, onClose, onReturned }) {
  const [loading, setLoading] = useState(false);
  const [returnDate, setReturnDate] = useState(new Date().toISOString().split('T')[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/asset-assignments/${assignment.id}/return`, {
        return_date: returnDate
      }, { withCredentials: true });
      onReturned();
      onClose();
    } catch (err) {
      console.error('Failed to return asset:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0a0a0f] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-white font-heading text-xl">Return Asset</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <div className="glass-card p-3 mb-4">
            <div className="text-white font-medium">{assignment.asset_name}</div>
            <div className="text-xs text-slate-400">
              Assigned to {assignment.employee_name} since {new Date(assignment.allocation_date).toLocaleDateString('en-IN')}
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Return Date *</label>
              <Input
                type="date"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                required
                className="bg-black/40 border-white/10 text-white"
                data-testid="return-date-input"
              />
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700">
                {loading ? 'Processing...' : 'Confirm Return'}
              </Button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Employee Assets Summary Sheet
function EmployeeAssetsSummary({ summary, onClose, onReturn, formatDate }) {
  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle className="text-white font-heading text-2xl font-bold" data-testid="employee-assets-title">
            {summary.employee_name}
          </SheetTitle>
          <div className="flex items-center gap-3">
            <span className="font-mono text-cyan-400 text-sm">{summary.employee_id}</span>
            {summary.department && (
              <span className="text-xs bg-white/10 px-2 py-1 rounded text-slate-400">
                {summary.department}
              </span>
            )}
          </div>
        </SheetHeader>

        <div className="space-y-4">
          {/* Summary Card */}
          <div className="glass-card p-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-400">Total Assets Assigned</span>
              <span className="text-3xl font-bold text-white">{summary.asset_count}</span>
            </div>
          </div>

          {/* Assets List */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400">
              Assigned Assets
            </h3>
            {summary.assets.map((assignment) => (
              <div 
                key={assignment.id} 
                className="glass-card p-4 flex items-center justify-between"
                data-testid={`summary-asset-${assignment.asset_id}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(getAssetCategory(assignment.asset_name))}
                    <span className="text-white font-medium">{assignment.asset_name}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs font-mono text-cyan-400">{assignment.asset_id}</span>
                    <span className="text-xs text-slate-500">
                      Since {formatDate(assignment.allocation_date)}
                    </span>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => onReturn(assignment)}
                  className="h-8 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/20"
                  data-testid={`return-summary-${assignment.asset_id}`}
                >
                  <RotateCcw size={14} className="mr-1" />
                  Return
                </Button>
              </div>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Helper to guess category from asset name (fallback)
function getAssetCategory(assetName) {
  const name = assetName.toLowerCase();
  if (name.includes('laptop') || name.includes('macbook') || name.includes('thinkpad')) return 'Laptop';
  if (name.includes('mouse') || name.includes('keyboard') || name.includes('headphone')) return 'Accessories';
  if (name.includes('monitor') || name.includes('printer') || name.includes('scanner')) return 'Equipment';
  if (name.includes('chair') || name.includes('desk') || name.includes('table')) return 'Furniture';
  return 'Other';
}


function EditAssetDialog({ asset, onClose, onUpdated }) {
  const [form, setForm] = useState({
    name: asset.name || '',
    category: asset.category || '',
    brand: asset.brand || '',
    model: asset.model || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/assets/${asset.id}`, form, { withCredentials: true });
      toast.success('Asset updated');
      onUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update asset');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white font-heading">Edit Asset</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Asset Name *</label>
            <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="bg-black/40 border-white/10 text-white" data-testid="edit-asset-name" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Category *</label>
            <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="edit-asset-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10">
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Brand</label>
              <Input value={form.brand} onChange={(e) => setForm(p => ({ ...p, brand: e.target.value }))} className="bg-black/40 border-white/10 text-white" data-testid="edit-asset-brand" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Model</label>
              <Input value={form.model} onChange={(e) => setForm(p => ({ ...p, model: e.target.value }))} className="bg-black/40 border-white/10 text-white" data-testid="edit-asset-model" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.name || !form.category} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-edit-asset-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
